import { User } from '../types';

export const USER_UPDATED_EVENT = 'user:updated';
export const USER_SYNC_REQUEST_EVENT = 'user:sync:request';

export interface UserUpdatedEventDetail {
  user: Partial<User>;
  timestamp: number;
}

export interface UserSyncRequestEventDetail {
  requestId: string;
  timestamp: number;
}

export const broadcastUserUpdate = (userUpdate: Partial<User>): void => {
  const event = new CustomEvent(USER_UPDATED_EVENT, {
    detail: {
      user: userUpdate,
      timestamp: Date.now(),
    } as UserUpdatedEventDetail,
  });
  
  window.dispatchEvent(event);
  try {
    const currentUserData = getUserFromStorage();
    const updatedUserData = { ...currentUserData, ...userUpdate };
    localStorage.setItem('studypal:user', JSON.stringify(updatedUserData));
  } catch (error) {
    console.warn('Failed to save user data to localStorage:', error);
  }
};

export const onUserUpdate = (callback: (userUpdate: Partial<User>) => void): (() => void) => {
  const handleUserUpdate = (event: CustomEvent<UserUpdatedEventDetail>) => {
    callback(event.detail.user);
  };

  window.addEventListener(USER_UPDATED_EVENT, handleUserUpdate as EventListener);
  
  return () => {
    window.removeEventListener(USER_UPDATED_EVENT, handleUserUpdate as EventListener);
  };
};

export const getUserFromStorage = (): Partial<User> | null => {
  try {
    const userData = localStorage.getItem('studypal:user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.warn('Failed to parse user data from localStorage:', error);
    return null;
  }
};

export const setUserInStorage = (user: User | null): void => {
  try {
    if (user) {
      localStorage.setItem('studypal:user', JSON.stringify(user));
    } else {
      localStorage.removeItem('studypal:user');
    }
  } catch (error) {
    console.warn('Failed to save user data to localStorage:', error);
  }
};

export const requestUserSync = (): void => {
  const event = new CustomEvent(USER_SYNC_REQUEST_EVENT, {
    detail: {
      requestId: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
    } as UserSyncRequestEventDetail,
  });
  
  window.dispatchEvent(event);
};

export const onUserSyncRequest = (getCurrentUser: () => User | null): (() => void) => {
  const handleSyncRequest = (event: CustomEvent<UserSyncRequestEventDetail>) => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      broadcastUserUpdate(currentUser);
    }
  };

  window.addEventListener(USER_SYNC_REQUEST_EVENT, handleSyncRequest as EventListener);
  
  return () => {
    window.removeEventListener(USER_SYNC_REQUEST_EVENT, handleSyncRequest as EventListener);
  };
};

export const useUserSync = () => {
  return {
    broadcastUserUpdate,
    onUserUpdate,
    getUserFromStorage,
    setUserInStorage,
    requestUserSync,
    onUserSyncRequest,
  };
};
